/**
 * "듣는중" 카드 안의 수음 파형 — §14.1.
 *
 * ■ 무엇을 그리나
 *   서버가 **마지막 프레임에서 실측한** dBFS(`utt.state.level_dbfs`)를 받아
 *   막대 높이로 그린다. 값이 올 때마다 오른쪽으로 흘러 최근 이력을 만든다.
 *
 * ■ 왜 서버 값인가 (이 파일의 존재 이유)
 *   클라이언트가 자체 계산한 마이크 레벨을 쓰면, 브라우저가 무음을 보내는
 *   동안에도 미터가 춤춘다 — 실제로 회의 하나가 통째로 그렇게 날아갔고
 *   화면은 내내 "정상"으로 보였다. 서버 실측만이 "서버에 소리가 닿고 있다"
 *   의 증거가 된다. 값이 안 오면 파형은 **멈춘다**. 멈춘 파형은 정직하다.
 *
 * ■ 지어내지 않는다
 *   보간·감쇠 애니메이션으로 "부드럽게" 만들지 않는다. 그러면 값이 끊겨도
 *   잠시 살아 있는 것처럼 보인다. 새 값이 올 때만 움직인다.
 */

import { useEffect, useRef, useState } from 'react'

/** 화면에 남기는 최근 표본 수(1s 주기 × 24 ≈ 24초). */
const SLOTS = 24
/** 이 아래는 사실상 무음으로 본다(서버 VAD 에너지 바닥과 같은 축). */
const FLOOR_DBFS = -60
/** 이 위는 꽉 찬 것으로 본다. */
const CEIL_DBFS = -6

/** dBFS → 0..1. 범위를 벗어나면 잘라낸다. */
export function levelRatio(dbfs: number): number {
  if (!Number.isFinite(dbfs)) return 0
  const r = (dbfs - FLOOR_DBFS) / (CEIL_DBFS - FLOOR_DBFS)
  return Math.max(0, Math.min(1, r))
}

type Props = {
  /** 서버 실측 음량. null/undefined 면 아무것도 그리지 않는다. */
  levelDbfs?: number | null
  /** 발화 id — 바뀌면 이력을 새로 시작한다. */
  utt: string
}

export function LevelWave({ levelDbfs, utt }: Props) {
  const [bars, setBars] = useState<number[]>([])
  const lastUtt = useRef(utt)

  useEffect(() => {
    if (lastUtt.current !== utt) {
      lastUtt.current = utt
      setBars([])
    }
  }, [utt])

  useEffect(() => {
    if (typeof levelDbfs !== 'number') return
    setBars((prev) => [...prev, levelRatio(levelDbfs)].slice(-SLOTS))
  }, [levelDbfs])

  // 서버가 값을 안 싣는 구 버전이면 그리지 않는다(§14 — 없는 것을 그리지 않는다).
  if (typeof levelDbfs !== 'number' && bars.length === 0) return null

  return (
    <div
      className="flex h-6 items-end gap-[2px]"
      data-testid="level-wave"
      data-bars={bars.length}
      aria-hidden="true"
    >
      {Array.from({ length: SLOTS }, (_, i) => {
        // 오른쪽이 최신 — 왼쪽은 비워 둔다(이력이 짧을 때).
        const off = SLOTS - bars.length
        const v = i >= off ? bars[i - off] : 0
        // 완전 0 도 1px 은 남겨 "축이 있다"를 보인다.
        const h = Math.max(1, Math.round((v ?? 0) * 22))
        return (
          <span
            key={i}
            className="w-[3px] rounded-full bg-primary/70"
            style={{ height: `${h}px` }}
          />
        )
      })}
    </div>
  )
}
